/**
 * Pandoc Fenced Div Utilities
 *
 * These utilities transform between Pandoc-style fenced divs and remark-directive syntax.
 *
 * Pandoc syntax:
 * - `::: {.class #id}` - attributes in braces
 * - `::: ClassName` - unbraced class name (treated as class)
 * - `::::: {.class}` - more than 3 colons
 * - `::: Warning ::::::` - trailing colons after attributes
 *
 * Directive syntax (remark-directive):
 * - `:::name{attrs}` - name followed by optional attributes
 *
 * Usage:
 * - Call `preprocessPandocDivs()` before loading markdown into the editor
 * - Call `postprocessPandocDivs()` before saving markdown
 */

/**
 * Escape colons in heading attribute keys to prevent remark-directive conflicts.
 *
 * remark-directive parses `:name` as a text directive, which conflicts with
 * Pandoc-style attribute keys like `epub:type`. This function escapes such
 * colons with a placeholder before remark parsing.
 *
 * @param markdown - The markdown content
 * @returns The markdown with escaped colons in heading attribute keys
 */
function escapeHeadingAttrColons(markdown: string): string {
  // Match heading lines with attribute blocks at the end: # Title {attrs}
  return markdown.replace(
    /^(#{1,6}\s+.*)\{([^}]+)\}\s*$/gm,
    (_match, headingPart: string, attrContent: string) => {
      // Replace key:name= patterns (like epub:type=) with key__COLON__name=
      const escapedAttrs = attrContent.replace(
        /([\w-]+):([\w-]+)\s*=/g,
        '$1__COLON__$2='
      );
      return `${headingPart}{${escapedAttrs}}`;
    }
  );
}

/**
 * Preprocess markdown to transform Pandoc-style fenced divs to remark-directive syntax
 * and escape colons in heading attribute keys.
 *
 * This must be called before the markdown is parsed by remark-directive.
 *
 * @param markdown - The markdown content with Pandoc fenced divs
 * @returns The markdown with directive-compatible syntax
 */
export function preprocessPandocDivs(markdown: string): string {
  let result = markdown
    // Case 1: `:::+ {attrs} :::*` → `:::div{attrs}`
    // Handles: `::: {.class}`, `::::: {#id .class} ::::::`, etc.
    .replace(
      /^:{3,}\s+(\{[^}]+\})\s*:*\s*$/gm,
      ':::div$1'
    )
    // Case 2: `:::+ ClassName :::*` → `:::ClassName`
    // Handles: `::: Warning`, `::::: Danger ::::::`, etc.
    // Only match if it's a valid identifier (starts with letter, contains word chars)
    .replace(
      /^:{3,}\s+([A-Za-z][\w-]*)\s*:*\s*$/gm,
      ':::$1'
    )
    // Normalize closing fences: `::::::` → `:::`
    // Closing fences have no attributes, just colons
    .replace(
      /^:{4,}\s*$/gm,
      ':::'
    );

  // Handle empty divs: add a zero-width space placeholder
  // Empty divs (opening immediately followed by closing) don't parse correctly
  // Pattern: `:::name{attrs}\n:::` or `:::name\n:::`
  result = result.replace(
    /^(:::[\w-]*(?:\{[^}]*\})?)\n(:::)\s*$/gm,
    '$1\n\u200B\n$2'
  );

  // Escape colons in heading attribute keys (epub:type → epub__COLON__type)
  // to prevent remark-directive from parsing `:name` as text directives
  result = escapeHeadingAttrColons(result);

  return result;
}

/**
 * Postprocess markdown to restore Pandoc-compatible syntax.
 *
 * This should be called before saving markdown. It handles:
 * 1. Restoring Pandoc fenced div syntax from remark-directive syntax
 * 2. Unescaping backslash-escaped colons in heading attribute keys
 *    (remark escapes `epub:type` → `epub\:type` to prevent directive parsing)
 * 3. Unescaping backslash-escaped underscores in template variables
 *    (remark escapes `_` → `\_` in text to prevent emphasis interpretation)
 *
 * @param markdown - The markdown content with directive syntax
 * @returns The markdown with Pandoc-compatible syntax
 */
export function postprocessPandocDivs(markdown: string): string {
  let result = markdown
    // Transform `:::div{attrs}` back to `::: {attrs}` (handle variable colon counts)
    .replace(
      /^:{3,}div(\{[^}]+\})\s*$/gm,
      '::: $1'
    )
    // Remove zero-width space placeholders from empty divs
    .replace(/\u200B/g, '');

  // Ensure blank lines around block-level constructs
  // remark-directive may serialize with only \n instead of \n\n between blocks
  // Also ensures blank lines after headings (safety net for serialization edge cases)
  // Run AFTER the :::div → ::: transform so we match the final format
  const lines = result.split('\n');
  const fixedLines: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const prevLine = fixedLines.length > 0 ? fixedLines[fixedLines.length - 1] : '';
    const isOpenDiv = /^:::\s/.test(line);
    const isCloseDiv = /^:::\s*$/.test(prevLine);
    const prevIsHeading = /^#{1,6}\s/.test(prevLine);

    // Insert blank line before opening ::: if previous line is not empty
    if (isOpenDiv && prevLine.trim() !== '') {
      fixedLines.push('');
    }
    // Insert blank line after closing ::: if current line is not empty and not another :::
    if (isCloseDiv && line.trim() !== '' && !isOpenDiv) {
      fixedLines.push('');
    }
    // Insert blank line after heading if next line is not empty
    // (headings must be followed by a blank line in well-formed markdown)
    if (prevIsHeading && line.trim() !== '') {
      fixedLines.push('');
    }

    fixedLines.push(line);
  }
  result = fixedLines.join('\n');

  // Unescape backslash-escaped colons in heading attribute blocks
  // remark serializes `epub:type` as `epub\:type` to avoid directive parsing
  result = result.replace(
    /^(#{1,6}\s+.*)\{([^}]+)\}\s*$/gm,
    (_match, headingPart: string, attrContent: string) => {
      const unescaped = attrContent.replace(/\\:/g, ':');
      return `${headingPart}{${unescaped}}`;
    }
  );

  // Unescape backslash-escaped underscores inside {{...}} template variables
  // remark serializes `{{VAR_NAME}}` as `{{VAR\_NAME}}` to avoid emphasis
  result = result.replace(
    /\{\{[^}]*\}\}/g,
    (match) => match.replace(/\\_/g, '_')
  );

  // Unescape backslash-escaped brackets in Pandoc citations
  // remark serializes `[@key]` as `\[@key]` to avoid link syntax
  result = result.replace(/\\\[@/g, '[@');

  return result;
}
